import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { SelectField } from '../../components/ui/SelectField';
import { audienceRuleKey, describeAudienceRule } from '../../lib/portalLibrary/audience';
import {
  deadRuleLabels,
  portalAccessSentence,
  reachSentence,
  sectionReachSentence,
} from '../../lib/portalLibrary/staffView';
import { INDUSTRIES, INDUSTRY_LABELS, type Account, type Industry } from '../../types/account';
import type { PortalAudienceRule, PortalLibrarySection } from '../../types/portalLibrary';
import { previewReach } from './api';

// Client portal library — who a tile is for.
//
// ⚠️ The industry list is enumerated FROM THE TYPE (INDUSTRIES), never typed by
// hand: all four values, so nobody in `other` is silently left out (brief §2).
//
// ⚠️⚠️ THE REACH IS STATED BEFORE ANYTHING IS CONFIRMED (Amendment 2 §D). Any
// signed-in staff may publish, so the honest control is showing the blast
// radius — an `everyone` rule fans out to every admitted client at once, which
// a shared link does not.

/**
 * The server-computed reach for a rule set. Shared by the editor (which shows
 * it) and the dialogs (which block on a refusal) through one query key, so the
 * two cannot disagree. A cheap roster read, not an AI call.
 */
export function useAudienceReach(rules: PortalAudienceRule[], section: PortalLibrarySection) {
  const key = rules.map(audienceRuleKey).sort().join('|');
  return useQuery({
    // ⚠️ Round 6 §3: the SECTION is in the key. The same rules reach a different
    // number of people in Monthly campaigns than in Campaigns & promotions, so a
    // cached answer for one section must never be read for another.
    queryKey: ['portal-library-reach', section, key],
    queryFn: () => previewReach(rules, section),
    enabled: rules.length > 0,
  });
}

type Kind = PortalAudienceRule['kind'];

const KIND_OPTIONS: { value: Kind; label: string }[] = [
  { value: 'everyone', label: 'Every client with portal access' },
  { value: 'industry', label: 'An industry' },
  { value: 'accountSubtree', label: 'A group or account (and everything under it)' },
  { value: 'marque', label: 'A marque / brand' },
];

/**
 * The rule PICKER on its own — the chosen rules and the add controls, no reach line.
 * ⚠️ Extracted (Run I) so the Inspiration uploader uses the SAME picker as the library: one
 * definition of how an audience is chosen. AudienceEditor below is this plus the library's reach.
 */
export function AudienceRulePicker({
  value,
  onChange,
  accounts,
  emptyHint = 'No audience yet — a tile with no audience is shown to nobody.',
}: {
  value: PortalAudienceRule[];
  onChange: (rules: PortalAudienceRule[]) => void;
  accounts: Account[];
  emptyHint?: string;
}) {
  const [kind, setKind] = useState<Kind>('marque');
  const [pick, setPick] = useState('');

  const nameById = useMemo(() => new Map(accounts.map((a) => [a.id, a.name])), [accounts]);
  const industryOptions = INDUSTRIES.map((i) => ({ value: i, label: INDUSTRY_LABELS[i] }));
  const accountOptions = useMemo(
    () =>
      accounts
        .map((a) => ({ value: a.id, label: a.name, description: a.accountType }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [accounts],
  );
  const brandOptions = accountOptions.filter((o) => o.description === 'brand');

  function add(): void {
    let rule: PortalAudienceRule | null = null;
    if (kind === 'everyone') rule = { kind: 'everyone' };
    else if (kind === 'industry' && pick) rule = { kind: 'industry', industry: pick as Industry };
    else if (kind === 'accountSubtree' && pick) rule = { kind: 'accountSubtree', accountId: pick };
    else if (kind === 'marque' && pick) rule = { kind: 'marque', brandAccountId: pick };
    if (!rule) return;
    const k = audienceRuleKey(rule);
    if (!value.some((r) => audienceRuleKey(r) === k)) onChange([...value, rule]);
    setPick('');
  }

  return (
    <div className="space-y-2">
      {value.length === 0 ? (
        <p className="text-xs text-slate-500">{emptyHint}</p>
      ) : (
        <ul className="space-y-1">
          {value.map((r) => (
            <li
              key={audienceRuleKey(r)}
              className="flex items-center justify-between rounded border border-slate-200 px-2 py-1 text-sm"
            >
              <span>{describeAudienceRule(r, nameById)}</span>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Remove ${describeAudienceRule(r, nameById)}`}
                onClick={() => onChange(value.filter((x) => audienceRuleKey(x) !== audienceRuleKey(r)))}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <SelectField
          value={kind}
          onValueChange={(v) => {
            setKind(v as Kind);
            setPick('');
          }}
          options={KIND_OPTIONS}
          size="sm"
          aria-label="Audience rule type"
        />
        {kind === 'industry' && (
          <SelectField
            value={pick}
            onValueChange={setPick}
            options={industryOptions}
            placeholder="Choose an industry"
            size="sm"
            aria-label="Industry"
          />
        )}
        {kind === 'accountSubtree' && (
          <Combobox
            value={pick}
            onValueChange={setPick}
            options={accountOptions}
            placeholder="Choose a group or account"
            searchPlaceholder="Search accounts…"
            size="sm"
            aria-label="Group or account"
          />
        )}
        {kind === 'marque' && (
          <Combobox
            value={pick}
            onValueChange={setPick}
            options={brandOptions}
            placeholder="Choose a marque"
            searchPlaceholder="Search brands…"
            size="sm"
            aria-label="Marque"
          />
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          disabled={kind !== 'everyone' && !pick}
        >
          Add
        </Button>
      </div>
    </div>
  );
}

export function AudienceEditor({
  value,
  onChange,
  accounts,
  section,
}: {
  value: PortalAudienceRule[];
  onChange: (rules: PortalAudienceRule[]) => void;
  accounts: Account[];
  /** The section the tile is going to — part of the reach (Round 6 §3). */
  section: PortalLibrarySection;
}) {
  const reach = useAudienceReach(value, section);
  const dead = reach.data ? deadRuleLabels(reach.data.reach) : [];

  return (
    <div className="space-y-2">
      <AudienceRulePicker value={value} onChange={onChange} accounts={accounts} />
      {value.length > 0 && (
        <div className="rounded bg-slate-50 px-2 py-1.5 text-sm" aria-live="polite">
          {reach.isPending ? (
            <span className="text-slate-500">Working out who this reaches…</span>
          ) : reach.isError ? (
            <span className="text-destructive">Couldn’t work out who this reaches.</span>
          ) : reach.data?.refusal ? (
            <span className="text-destructive">{reach.data.refusal}</span>
          ) : reach.data ? (
            <>
              <span className="font-medium text-slate-900">{reachSentence(reach.data.reach)}</span>{' '}
              {/* ⚠️ Round 6 §3: how many of them have the chosen SECTION. Said
                  only when it is fewer than all of them; zero is the refusal. */}
              {sectionReachSentence(reach.data.reach) && (
                <span className="text-amber-700">{sectionReachSentence(reach.data.reach)} </span>
              )}
              {/* ⚠️ The SECOND number — who can actually open it today. Same
                  server call, same match set: it is a subset of the first. */}
              <span
                className={
                  reach.data.reach.accountsWithPortalAccess === 0
                    ? 'text-amber-700'
                    : 'text-slate-700'
                }
              >
                {portalAccessSentence(reach.data.reach)}
              </span>
              {dead.length > 0 && (
                <span className="block text-xs text-amber-700">
                  {dead.map((d) => `“${d}”`).join(', ')} matched nothing on its own.
                </span>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

/**
 * Can the dialog submit this audience? Null = yes; otherwise the reason the
 * button is disabled. ⚠️ While the reach is unknown, it is NOT yet allowed —
 * the whole point is that the number is read before confirming.
 */
export function audienceBlocker(
  rules: PortalAudienceRule[],
  reach: ReturnType<typeof useAudienceReach>,
): string | null {
  if (rules.length === 0) return 'Choose who this is for.';
  if (reach.isPending) return 'Working out who this reaches…';
  if (reach.isError || !reach.data) return 'Couldn’t work out who this reaches.';
  return reach.data.refusal;
}
